import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Globe, Settings } from 'lucide-react';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { FlavourFormData, Flavour } from '../types';
import { mergeThemeColors } from '../constants/themeColors';
import { AppCard } from '../components/Apps';
import AppCreationWizard, { AppCreationFormData } from '../components/Apps/AppCreationWizard';

const Apps: React.FC = () => {
  const { availableFlavours, currentFlavourId, setCurrentFlavourId, refreshFlavours, user, isLoading } = useAuth();
  
  // Check user role and permissions
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin';
  const isSubAdmin = user?.role === 'sub_admin';
  
  // Filter flavours based on user role and assigned apps
  const filteredFlavours = React.useMemo(() => {
    if (isSuperAdmin) {
      // Super admin can see all apps
      return availableFlavours;
    } else if (isAdmin || isSubAdmin) {
      // Admin/Sub-admin can only see apps they're assigned to
      if (user?.flavourIds && user.flavourIds.length > 0) {
        return availableFlavours.filter(flavour =>
          user.flavourIds!.includes(parseInt(flavour.id))
        );
      }
      return [];
    }
    return [];
  }, [availableFlavours, user, isSuperAdmin, isAdmin, isSubAdmin]);
  
  const [isCreateWizardOpen, setIsCreateWizardOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingFlavour, setEditingFlavour] = useState<Flavour | null>(null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [packageNameStatus, setPackageNameStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [packageNameError, setPackageNameError] = useState<string | null>(null);

  // NOTE: previously this component held its own `appIconPreview`,
  // `onboardBgPreview`, `homeBgPreview`, and `themeColors` state plus a
  // `getThemeColorsWithDefaults()` helper. They were leftovers from the
  // pre-wizard inline edit form. The wizard (`AppCreationWizard`) now
  // manages all of those internally — keeping them here led to a real
  // bug where `onSubmitEdit` overwrote the wizard's fresh colors with
  // the stale local `themeColors` state. They've been removed; do not
  // re-introduce per-field state in this component for the edit flow.

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FlavourFormData>();

  // Refresh flavours when user flavour IDs change (but not on initial load)
  const prevUserFlavourIds = React.useRef<number[] | undefined>();
  useEffect(() => {
    if (user && user.flavourIds) {
      // Only refresh if flavourIds actually changed (not on initial load)
      const flavourIdsChanged = JSON.stringify(prevUserFlavourIds.current) !== JSON.stringify(user.flavourIds);
      if (flavourIdsChanged && prevUserFlavourIds.current !== undefined) {
        refreshFlavours();
      }
      prevUserFlavourIds.current = user.flavourIds;
    }
  }, [user?.flavourIds, refreshFlavours]);

  // If there's only one visible app, automatically set it as the current app
  useEffect(() => {
    if (filteredFlavours.length === 1) {
      const onlyFlavour = filteredFlavours[0];
      if (onlyFlavour && currentFlavourId !== onlyFlavour.id) {
        setCurrentFlavourId(onlyFlavour.id);
      }
    }
  }, [filteredFlavours, currentFlavourId, setCurrentFlavourId]);


  const watchedPackageName = watch('packageName');
  // Check package name availability with debouncing
  useEffect(() => {
    const checkPackageName = async () => {
      if (!watchedPackageName || watchedPackageName.length < 3) {
        setPackageNameStatus('idle');
        setPackageNameError(null);
        return;
      }

      setPackageNameStatus('checking');
      setPackageNameError(null);
      
      try {
        const isAvailable = await apiService.checkPackageNameAvailability(watchedPackageName);
        setPackageNameStatus(isAvailable ? 'available' : 'taken');
      } catch (error: any) {
        // Check if it's a validation error (format issue)
        if (error.message && error.message.includes('format')) {
          setPackageNameStatus('invalid');
          setPackageNameError(error.message);
        } else {
          setPackageNameStatus('taken');
          setPackageNameError('Unable to check package name availability');
        }
      }
    };

    // Only check if form is mounted and we have a valid package name
    if (watchedPackageName) {
      const timeoutId = setTimeout(checkPackageName, 500); // Increased debounce time
      return () => clearTimeout(timeoutId);
    }
  }, [watchedPackageName]);

  // Show loading state while user data is being fetched
  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  const getPackageNameMessage = () => {
    if (!watchedPackageName) return null;
    
    switch (packageNameStatus) {
      case 'checking':
        return 'Checking availability...';
      case 'available':
        return 'Package name is available!';
      case 'taken':
        return 'Package name is already taken';
      case 'invalid':
        return packageNameError || 'Invalid package name format';
      default:
        return null;
    }
  };



  const resetForm = () => {
    reset();
    setPackageNameStatus('idle');
    setPackageNameError(null);
  };

  // Function to trigger package name validation when modal opens
  const triggerPackageNameValidation = async (packageName: string) => {
    if (!packageName || packageName.length < 3) {
      setPackageNameStatus('idle');
      setPackageNameError(null);
      return;
    }

    setPackageNameStatus('checking');
    setPackageNameError(null);
    
    try {
      const isAvailable = await apiService.checkPackageNameAvailability(packageName);
      setPackageNameStatus(isAvailable ? 'available' : 'taken');
    } catch (error: any) {
      // Check if it's a validation error (format issue)
      if (error.message && error.message.includes('format')) {
        setPackageNameStatus('invalid');
        setPackageNameError(error.message);
      } else {
        setPackageNameStatus('taken');
        setPackageNameError('Unable to check package name availability');
      }
    }
  };


  const onSubmitWizard = async (data: any) => {
    setIsFormLoading(true);
    try {
      // Convert wizard data to FlavourFormData format
      const flavourData: FlavourFormData = {
        name: data.name,
        description: data.description || '',
        packageName: data.packageName,
        appIcon: data.appIcon,
        title: data.title,
        themeColors: data.themeColors,
        platform: data.platform,
        pipelineId: data.pipelineId,
        iosPipelineId: data.iosPipelineId,
        googleClientApiAndroid: data.googleClientApiAndroid,
        googleClientApiIos: data.googleClientApiIos,
        mobileProvisionName: data.mobileProvisionName,
        mobileProvisionUUID: data.mobileProvisionUUID,
        // Backend will automatically determine step completion based on provided data
        // Add optional fields that might be present
        onboardBg: data.onboardBg,
        homeBg: data.homeBg,
        // Include Firebase files if present
        googleServicesJson: data.googleServicesJson,
        googleServicesPlist: data.googleServicesPlist,
        // Include keystore data if generated (including buffer for upload)
        ...(data.keystoreUrl && { keystoreUrl: data.keystoreUrl }),
        ...(data.keystoreSha1 && { keystoreSha1: data.keystoreSha1 }),
        ...(data.keystoreSha256 && { keystoreSha256: data.keystoreSha256 }),
        ...(data.keystorePassword && { keystorePassword: data.keystorePassword }),
        ...(data.keystoreAlias && { keystoreAlias: data.keystoreAlias }),
        ...(data.keystoreBuffer && { keystoreBuffer: data.keystoreBuffer }),
        stepsDone: data.stepsDone,
        // Version fields
        ...(data.androidVersionCode && { androidVersionCode: data.androidVersionCode }),
        ...(data.androidVersionName && { androidVersionName: data.androidVersionName }),
        ...(data.iosVersionCode && { iosVersionCode: data.iosVersionCode }),
      };

      await apiService.createOrUpdateFlavour(flavourData, true);
      toast.success('App created successfully! Build process has been initiated.');
      setIsCreateWizardOpen(false);
      await refreshFlavours();
    } catch (error) {
      toast.error('Failed to create app');
    } finally {
      setIsFormLoading(false);
    }
  };

  const onSaveDraftWizard = async (data: any) => {
    setIsFormLoading(true);
    try {
      // Convert wizard data to FlavourFormData format for draft
      const flavourData: FlavourFormData = {
        name: data.name,
        description: data.description || '',
        packageName: data.packageName,
        platform: data.platform,
        appIcon: data.appIcon,
        title: data.title,
        themeColors: data.themeColors,
        pipelineId: data.pipelineId,
        iosPipelineId: data.iosPipelineId,
        mobileProvisionName: data.mobileProvisionName,
        mobileProvisionUUID: data.mobileProvisionUUID,
        // Add optional fields that might be present
        onboardBg: data.onboardBg,
        homeBg: data.homeBg,
        // Add Firebase configuration fields
        googleServicesJson: data.googleServicesJson,
        googleServicesPlist: data.googleServicesPlist,
        // Add keystore fields
        keystoreUrl: data.keystoreUrl,
        keystoreSha1: data.sha1,
        keystoreSha256: data.sha256,
        keystorePassword: data.keystorePassword,
        keystoreAlias: data.keystoreAlias,
        keystoreBuffer: data.keystoreBuffer,
        // Add step information
        stepsDone: data.stepsDone,
      };

      await apiService.createOrUpdateFlavour(flavourData, false); // Save as draft
      toast.success('Draft saved successfully!');
      setIsCreateWizardOpen(false);
      await refreshFlavours();
    } catch (error) {
      toast.error('Failed to save draft');
    } finally {
      setIsFormLoading(false);
    }
  };

  const onSubmitEdit = async (data: FlavourFormData, options?: { closeModal?: boolean }) => {
    if (!editingFlavour) return;

    // Package name cannot be changed during edit, so no need to validate it
    // The package name will remain the same as the original flavour

    const shouldCloseModal = options?.closeModal !== false;

    setIsFormLoading(true);
    try {
      // The wizard always sends the user's edited colors via `data.themeColors`
      // (its `handleSaveDraft` / `handleFormSubmit` build the PRIMARY/SECONDARY/
      // ACCENT trio, falling back to DEFAULT_THEME_COLORS per-slot). Merge with
      // defaults again here so any missing slot still gets a default value.
      const finalThemeColors = mergeThemeColors(data.themeColors || {});
      // Use updateFlavour method that handles FormData for file uploads
      await apiService.updateFlavour(editingFlavour.id, {
        ...data,
        packageName: editingFlavour.packageName, // Keep original package name
        themeColors: finalThemeColors,
      });
      toast.success(shouldCloseModal ? 'App updated successfully!' : 'Changes saved successfully!');
      if (shouldCloseModal) {
        setIsEditModalOpen(false);
        setEditingFlavour(null);
        resetForm();
      }
      await refreshFlavours();
    } catch (error) {
      toast.error(shouldCloseModal ? 'Failed to update app' : 'Failed to save changes');
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleEdit = (flavour: Flavour) => {
    // Find the freshest version from availableFlavours in case the card
    // captured a stale reference. This is what unblocks the "edit colors,
    // save, reopen → see saved colors" flow when the AppCard's closure has
    // an older snapshot of the flavour.
    const fromContext = availableFlavours.find((f: Flavour) => String(f.id) === String(flavour.id));
    const flavourToEdit = fromContext || flavour;
    setEditingFlavour(flavourToEdit);
    // Seed the react-hook-form fields that aren't owned by the wizard's own
    // populate effect. Image previews and theme colors are intentionally NOT
    // seeded here — the wizard reads them from `editData` and manages its own
    // state for those.
    reset({
      name: flavourToEdit.name,
      description: flavourToEdit.description || '',
      packageName: flavourToEdit.packageName || '',
      title: flavourToEdit.title || '',
    });
    setPackageNameStatus('available'); // Package name is available since it can't be changed
    setIsEditModalOpen(true);
  };

  const handleDelete = async (flavourId: string) => {
    if (!window.confirm('Are you sure you want to delete this app? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteFlavour(flavourId);
      toast.success('App deleted successfully!');
      await refreshFlavours();
    } catch (error) {
      toast.error('Failed to delete app');
    }
  };

  const handleSwitchApp = (flavourId: string) => {
    const flavour = availableFlavours.find(f => f.id === flavourId);
    if (!flavour) {
      toast.error('App not found');
      return;
    }
    
    setCurrentFlavourId(flavourId);
    toast.success('Switched to new app!');
  };

  return (
    <div className="">
      <div className="flex justify-between items-center mt-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Apps Management</h1>
          <p className="text-gray-600">Manage your Trippy tour applications and switch between them</p>
        </div>
        {isSuperAdmin && (
          <div className="flex space-x-3">
            <Button onClick={() => {
              setIsCreateWizardOpen(true);
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Create New App
            </Button>
          </div>
        )}
        {!isSuperAdmin && filteredFlavours.length === 0 && (
          <div className="text-sm text-gray-500">
            No apps assigned to you. Contact your super admin to get access.
          </div>
        )}
      </div>

      {/* Current App Display */}
      {currentFlavourId && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mt-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Globe className="h-6 w-6 text-primary" />
              <div>
                <h3 className="font-semibold text-primary-900">Current App</h3>
                <p className="text-primary-700">
                  {filteredFlavours.find(f => f.id === currentFlavourId)?.name || 'Unknown App'}
                </p>
              </div>
            </div>
            {isSuperAdmin && (
              <button
                onClick={() => {
                  const currentFlavour = filteredFlavours.find(f => f.id === currentFlavourId);
                  if (currentFlavour) {
                    handleEdit(currentFlavour);
                  }
                }}
                className="p-1 text-primary-600 hover:text-primary-800 transition-colors"
                title="Edit current app settings"
              >
                <Settings className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Apps Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-3">
        {filteredFlavours.map((flavour) => (
          <AppCard
            key={flavour.id}
            flavour={flavour}
            isCurrentApp={currentFlavourId === flavour.id}
            isSuperAdmin={isSuperAdmin}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSwitch={handleSwitchApp}
            canSwitch={filteredFlavours.length > 1}
          />
        ))}
      </div>
      
      {/* No Apps Message */}
      {filteredFlavours.length === 0 && (
        <div className="text-center py-12">
          <div className="bg-gray-50 rounded-lg p-8">
            <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isSuperAdmin ? 'No Apps Created Yet' : 'No Apps Assigned'}
            </h3>
          </div>
        </div>
      )}



      {/* App Creation Wizard */}
      <AppCreationWizard
        isOpen={isCreateWizardOpen}
        onClose={() => {
          setIsCreateWizardOpen(false);
          resetForm();
        }}
        onSubmit={onSubmitWizard}
        onSaveDraft={onSaveDraftWizard}
        isLoading={isFormLoading}
      />

      {/* App Edit Wizard */}
      <AppCreationWizard
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingFlavour(null);
          resetForm();
        }}
        onSubmit={async (data: AppCreationFormData) => {
          // Convert AppCreationFormData to FlavourFormData
          const flavourData: FlavourFormData = {
            name: data.name,
            description: data.description || '',
            packageName: data.packageName,
            platform: data.platform,
            appIcon: data.appIcon,
            title: data.title,
            themeColors: data.themeColors,
            stepsDone: data.stepsDone,
            // Add background images (for updates)
            onboardBg: data.onboardBg,
            homeBg: data.homeBg,
            // Add Firebase configuration fields
            googleServicesJson: data.googleServicesJson,
            googleServicesPlist: data.googleServicesPlist,
            pipelineId: data.pipelineId,
            iosPipelineId: data.iosPipelineId,
            // Add iOS provisioning profile fields
            mobileProvisionName: data.mobileProvisionName,
            mobileProvisionUUID: data.mobileProvisionUUID,
            // Add keystore fields
            keystoreUrl: data.keystoreUrl,
            keystoreSha1: data.sha1,
            keystoreSha256: data.sha256,
            keystorePassword: data.keystorePassword,
            keystoreAlias: data.keystoreAlias,
            keystoreBuffer: data.keystoreBuffer,
            // Version fields
            androidVersionCode: data.androidVersionCode,
            androidVersionName: data.androidVersionName,
            iosVersionCode: data.iosVersionCode,
          };
          await onSubmitEdit(flavourData);
        }}
        onSaveDraft={async (data: AppCreationFormData) => {
          // Convert AppCreationFormData to FlavourFormData for draft save
          const flavourData: FlavourFormData = {
            name: data.name,
            description: data.description || '',
            packageName: data.packageName,
            platform: data.platform,
            appIcon: data.appIcon,
            title: data.title,
            themeColors: data.themeColors,
            stepsDone: data.stepsDone,
            // Add background images (for updates)
            onboardBg: data.onboardBg,
            homeBg: data.homeBg,
            // Add Firebase configuration fields
            googleServicesJson: data.googleServicesJson,
            googleServicesPlist: data.googleServicesPlist,
            pipelineId: data.pipelineId,
            iosPipelineId: data.iosPipelineId,
            // Add iOS provisioning profile fields
            mobileProvisionName: data.mobileProvisionName,
            mobileProvisionUUID: data.mobileProvisionUUID,
            // Add keystore fields
            keystoreUrl: data.keystoreUrl,
            keystoreSha1: data.sha1,
            keystoreSha256: data.sha256,
            keystorePassword: data.keystorePassword,
            keystoreAlias: data.keystoreAlias,
            keystoreBuffer: data.keystoreBuffer,
            // Version fields
            androidVersionCode: data.androidVersionCode,
            androidVersionName: data.androidVersionName,
            iosVersionCode: data.iosVersionCode,
          };
          // Save Changes in edit mode: persist changes but keep the popup open
          // so the user can continue editing.
          await onSubmitEdit(flavourData, { closeModal: false });
        }}
        isLoading={isFormLoading}
        isEditMode={true}
        editId={editingFlavour?.id}
        editData={editingFlavour ? (() => {
          return {
          name: editingFlavour.name,
          description: editingFlavour.description || '',
          packageName: editingFlavour.packageName || '',
          platform: editingFlavour.platform,
          title: editingFlavour.title || '',
          themeColors: editingFlavour.themeColors,
          stepsDone: editingFlavour.stepsDone?.toString() || '1',
          appIconUrl: editingFlavour.appIconUrl,
          onboardBgUrl: editingFlavour.onboardBgUrl,
          homeBgUrl: editingFlavour.homeBgUrl,
          // Firebase Configuration fields
          sha1: editingFlavour.keystoreSha1 || '',
          sha256: editingFlavour.keystoreSha256 || '',
          googleServicesJsonUrl: editingFlavour.googleServicesJsonUrl,
          googleServicesPlistUrl: editingFlavour.googleServicesPlistUrl,
          keystoreUrl: editingFlavour.keystoreUrl,
          pipelineId: editingFlavour.pipelineId,
          iosPipelineId: editingFlavour.iosPipelineId,
          mobileProvisionName: editingFlavour.mobileProvisionName,
          mobileProvisionUUID: editingFlavour.mobileProvisionUUID,
          buildId: editingFlavour.buildId,
          buildStatus: editingFlavour.buildStatus,
          iosBuildId: editingFlavour.iosBuildId,
          iosBuildStatus: editingFlavour.iosBuildStatus,
          androidVersionCode: editingFlavour.androidVersionCode,
          androidVersionName: editingFlavour.androidVersionName,
          iosVersionCode: editingFlavour.iosVersionCode,
          };
        })() : undefined}
      />
    </div>
  );
};

export default Apps; 